import { useAuth } from '@/app/AuthContext.jsx';
import {
  acceptInvitationRequest,
  apiErrorName,
  invitationPreviewRequest,
  rejectInvitationRequest,
} from '@/lib/recepthor-api';
import { Button, ConfirmDialog, Input, Spinner } from '@/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import BrandPane from './BrandPane.jsx';
import { validatePassword } from './passwordValidation';
import TermsAcceptField from './TermsAcceptField.jsx';
import TermsAndConditions, {
  FALLBACK_TYC_VERSION,
} from './terms-and-conditions.jsx';
import { useTermsCatalog } from './useTermsCatalog.js';

// Error names the accept answers with. Confirmed by the API team (REC-1807);
// adjust here if they change.
const ERR_TERMS_NOT_ACCEPTED = 'TermsNotAccepted';
const ERR_ACCOUNT_SETUP_REQUIRED = 'AccountSetupRequired';
const ERR_VALIDATION = 'ValidationError';
const ERR_TERMS_VERSION = 'TermsVersionNotFound';

const MSG_TERMS_REQUIRED =
  'Debes aceptar los términos y condiciones vigentes para unirte.';
const MSG_SETUP_REQUIRED =
  'Faltan datos para activar la cuenta. Completa el nombre, el apellido, la contraseña y acepta los términos y condiciones.';
const MSG_TERMS_LOCAL =
  'Debes aceptar los términos y condiciones para continuar.';
// Messages about the terms box: ticking the box again retires them.
const TERMS_MESSAGES = [
  MSG_TERMS_REQUIRED,
  MSG_SETUP_REQUIRED,
  MSG_TERMS_LOCAL,
];
const MSG_TOKEN_INVALID = 'El enlace de invitación no es válido o ya venció.';
const MSG_TYC_VERSION =
  'No pudimos validar la versión de los términos. Recarga la página o contacta a soporte.';

// True when a ValidationError body flags the password field. The issues list
// may sit under error.details, details or the body root; a path is an array
// of keys or a dotted string.
function hasPasswordIssue(body) {
  const issues =
    body?.error?.details?.issues ?? body?.details?.issues ?? body?.issues;
  if (!Array.isArray(issues)) return false;
  return issues.some((issue) => {
    const path = issue?.path;
    return Array.isArray(path)
      ? path.includes('password')
      : String(path ?? '')
          .split('.')
          .includes('password');
  });
}

function InvalidInvitationPane({ message, title = 'Enlace inválido' }) {
  return (
    <div className="login-form-pane">
      <div className="login-form">
        <p className="login-eyebrow">Invitación</p>
        <h1 className="login-heading">{title}</h1>
        <p className="recover-subtitle">{message}</p>
        <Link to="/login" className="login-forgot">
          Volver a inicio de sesión
        </Link>
      </div>
    </div>
  );
}

function RejectedPane({ isAuthenticated }) {
  return (
    <div className="login-form-pane">
      <div className="login-form">
        <p className="login-eyebrow">Invitación</p>
        <h1 className="login-heading">Invitación rechazada</h1>
        <p className="recover-subtitle">
          Rechazaste la invitación. Pueden volver a invitarte si cambias de
          opinión.
        </p>
        {isAuthenticated ? (
          <Link to="/" className="login-forgot">
            Volver al inicio
          </Link>
        ) : (
          <Link to="/login" className="login-forgot">
            Volver a inicio de sesión
          </Link>
        )}
      </div>
    </div>
  );
}

function AccountMismatchPane({ invitedEmail, currentEmail, onLogout }) {
  return (
    <div className="login-form-pane">
      <div className="login-form">
        <p className="login-eyebrow">Invitación</p>
        <h1 className="login-heading">Cuenta distinta</h1>
        <p className="recover-subtitle">
          Esta invitación es para {invitedEmail}. Estás conectado como{' '}
          {currentEmail}.
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Link to="/" className="login-forgot">
            Cancelar
          </Link>
          <Button variant="seal" size="lg" onClick={onLogout}>
            Cerrar sesión y continuar
          </Button>
        </div>
      </div>
    </div>
  );
}

function InvitationForm({ token, preview }) {
  const navigate = useNavigate();
  const { isAuthenticated, refreshUser, logout, user } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [confirmingReject, setConfirmingReject] = useState(false);
  // Same flow-init read and fallback as the activation form.
  const { catalog, loading: catalogLoading } = useTermsCatalog();
  const tycVersion =
    catalog?.version && catalog?.pdfUrl
      ? catalog.version
      : FALLBACK_TYC_VERSION;

  if (rejected) return <RejectedPane isAuthenticated={isAuthenticated} />;

  // The API binds the accept to the token and ignores the session. Without
  // this check, a logged-in user could accept an invitation addressed to
  // another account and see no sign that it landed there.
  if (
    isAuthenticated &&
    user?.email &&
    user.email.trim().toLowerCase() !==
      String(preview.email ?? '')
        .trim()
        .toLowerCase()
  ) {
    return (
      <AccountMismatchPane
        invitedEmail={preview.email}
        currentEmail={user.email}
        onLogout={logout}
      />
    );
  }

  // The messages about the box belong to the attempt that raised them: ticking
  // the box again retires them, unticking leaves them. Any other error stays.
  function handleTermsChange() {
    const next = !termsAccepted;
    setTermsAccepted(next);
    if (next) setError((prev) => (TERMS_MESSAGES.includes(prev) ? '' : prev));
  }

  async function handleAccept(e) {
    e.preventDefault();
    setError('');

    const payload = { token };
    if (preview.requiresAccountSetup) {
      if (catalogLoading) return;
      if (!firstName.trim()) {
        setError('El nombre es requerido.');
        return;
      }
      if (!lastName.trim()) {
        setError('El apellido es requerido.');
        return;
      }
      const validationError = validatePassword(password);
      if (validationError) {
        setError(validationError);
        return;
      }
      if (password !== confirmPassword) {
        setError('Las contraseñas no coinciden.');
        return;
      }
      if (!termsAccepted) {
        setError(MSG_TERMS_LOCAL);
        return;
      }
      payload.termsAccepted = true;
      payload.tycVersion = tycVersion;
      payload.firstName = firstName.trim();
      payload.lastName = lastName.trim();
      payload.password = password;
    }

    setBusy(true);
    try {
      const response = await acceptInvitationRequest(payload);

      if (response.ok) {
        if (isAuthenticated) {
          await refreshUser();
          navigate('/');
          return;
        }
        navigate('/login', { state: { invitationAccepted: true } });
        return;
      }

      const body = await Promise.resolve(response.json?.()).catch(() => null);
      const errorName = apiErrorName(body);

      if (errorName === ERR_TERMS_NOT_ACCEPTED) {
        // Uncheck so the message matches the box and the user re-ticks it.
        setTermsAccepted(false);
        setError(MSG_TERMS_REQUIRED);
      } else if (errorName === ERR_ACCOUNT_SETUP_REQUIRED) {
        setTermsAccepted(false);
        setError(MSG_SETUP_REQUIRED);
      } else if (errorName === ERR_VALIDATION && hasPasswordIssue(body)) {
        // Password rejected by the API: show the rules.
        setError(validatePassword(''));
      } else if (errorName === ERR_TERMS_VERSION) {
        setError(MSG_TYC_VERSION);
      } else if (response.status === 404) {
        // InvitationTokenInvalid, and any 404 without a known name.
        setError(MSG_TOKEN_INVALID);
      } else if (response.status === 400 && !errorName) {
        setError(MSG_SETUP_REQUIRED);
      } else {
        setError('No se pudo aceptar la invitación. Intenta nuevamente.');
      }
    } catch {
      setError('Error de conexión. Verifica tu internet e intenta nuevamente.');
    } finally {
      setBusy(false);
    }
  }

  // Rejecting deletes the membership in the API, so Rechazar only opens the
  // dialog; its confirm button sends the request. At 1280-1440 px the confirm
  // button opens under the cursor that clicked Rechazar, so a click that is
  // part of a double click (detail > 1) does not count; the keyboard sends 0.
  async function handleConfirmReject(event) {
    if (busy || event?.detail > 1) return;
    setError('');
    setBusy(true);
    try {
      const response = await rejectInvitationRequest(token);
      if (response.ok) {
        setRejected(true);
        return;
      }
      setError('No se pudo rechazar la invitación. Intenta nuevamente.');
    } catch {
      setError('Error de conexión. Verifica tu internet e intenta nuevamente.');
    } finally {
      setBusy(false);
      setConfirmingReject(false);
    }
  }

  return (
    <div className="login-form-pane">
      <form className="login-form" onSubmit={handleAccept}>
        <p className="login-eyebrow">Invitación</p>
        <h1 className="login-heading">Únete a {preview.organizationName}</h1>
        <p className="recover-subtitle">{preview.email}</p>
        <div className="login-fields">
          {preview.requiresAccountSetup && (
            <>
              <div className="login-field">
                <label className="login-label" htmlFor="invite-first-name">
                  Nombre *
                </label>
                <Input
                  id="invite-first-name"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="login-field">
                <label className="login-label" htmlFor="invite-last-name">
                  Apellido *
                </label>
                <Input
                  id="invite-last-name"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
              <div className="login-field">
                <label className="login-label" htmlFor="invite-password">
                  Nueva contraseña *
                </label>
                <Input
                  id="invite-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="login-field">
                <label className="login-label" htmlFor="invite-confirm">
                  Confirmar contraseña *
                </label>
                <Input
                  id="invite-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <TermsAcceptField
                checked={termsAccepted}
                onChange={handleTermsChange}
                disabled={catalogLoading}
                onOpenTerms={() => setShowTerms(true)}
              />
            </>
          )}
          {error && (
            <p className="login-alert login-alert--err" role="alert">
              {error}
            </p>
          )}
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              disabled={busy}
              onClick={() => setConfirmingReject(true)}
            >
              Rechazar
            </Button>
            <Button
              type="submit"
              variant="seal"
              size="lg"
              disabled={
                busy ||
                (preview.requiresAccountSetup &&
                  (!termsAccepted || catalogLoading))
              }
              className="login-submit"
            >
              {busy ? 'Procesando…' : 'Aceptar'}
            </Button>
          </div>
        </div>
      </form>
      {confirmingReject && (
        <ConfirmDialog
          title="Rechazar invitación"
          body="¿Rechazar la invitación? Para unirte después, el estudio tendrá que invitarte de nuevo."
          confirmLabel={busy ? 'Rechazando…' : 'Rechazar invitación'}
          cancelLabel="Volver"
          confirmDisabled={busy}
          onConfirm={handleConfirmReject}
          onCancel={() => {
            if (!busy) setConfirmingReject(false);
          }}
        />
      )}
      {preview.requiresAccountSetup && (
        <TermsAndConditions
          catalog={catalog}
          loading={catalogLoading}
          show={showTerms}
          onClose={() => setShowTerms(false)}
        />
      )}
    </div>
  );
}

function usePreview(token) {
  const [state, setState] = useState({
    loading: true,
    preview: null,
    error: '',
  });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, preview: null, error: '' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await invitationPreviewRequest(token);
        if (cancelled) return;
        if (response.ok) {
          const preview = await response.json();
          setState({ loading: false, preview, error: '' });
          return;
        }
        setState({
          loading: false,
          preview: null,
          error:
            response.status === 429
              ? 'Demasiados intentos. Espera un momento e intenta nuevamente.'
              : 'El enlace de invitación no es válido o ya venció.',
          title: response.status === 429 ? 'Demasiados intentos' : undefined,
        });
      } catch {
        if (cancelled) return;
        setState({
          loading: false,
          preview: null,
          error:
            'Error de conexión. Verifica tu internet e intenta nuevamente.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return state;
}

export default function AceptarInvitacion() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { loading, preview, error, title } = usePreview(token);
  // Until the session check resolves, isAuthenticated reads false and the
  // form would skip the account-mismatch check.
  const authLoading = useAuth()?.loading;

  return (
    <div className="login-page">
      <BrandPane />
      {!token ? (
        <InvalidInvitationPane message="El enlace de invitación no es válido. Verifica el link recibido por correo." />
      ) : loading || authLoading ? (
        <div className="login-form-pane">
          <Spinner size={48} />
        </div>
      ) : error ? (
        <InvalidInvitationPane message={error} title={title} />
      ) : (
        <InvitationForm token={token} preview={preview} />
      )}
    </div>
  );
}
